import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import "./Dashboard.css";

/**
 * ProjectContextMenu - Floating context menu for project actions
 * @param {boolean} open - Whether the menu is visible
 * @param {Object} position - { x, y } absolute screen position
 * @param {Object} project - The project object
 * @param {Function} onRename - Called with (project) to open rename dialog
 * @param {Function} onArchive - Called with (project) to archive/unarchive
 * @param {Function} onDelete - Called with (project) to open delete confirmation
 * @param {Function} onClose - Close callback
 * @param {boolean} darkMode - Dark theme flag
 */
export default function ProjectContextMenu({
  open,
  position,
  project,
  onRename,
  onArchive,
  onDelete,
  onClose,
  darkMode = false,
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open || !project || typeof document === "undefined") return null;

  const menuBg = darkMode ? "#1e293b" : "#ffffff";
  const menuBorder = darkMode ? "#334155" : "#e2e8f0";
  const textColor = darkMode ? "#e2e8f0" : "#1e293b";
  const hoverBg = darkMode ? "#334155" : "#f1f5f9";
  const dangerColor = "#ef4444";
  const dangerHoverBg = darkMode ? "#450a0a" : "#fff1f2";

  const isArchived = !!project.archived;


  const menuWidth = 176;
  const estimatedHeight = 128;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(position.x, vw - menuWidth - 8);
  const top = Math.min(position.y, vh - estimatedHeight - 8);

  const itemStyle = (color = textColor, hbg = hoverBg) => ({
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    width: "100%",
    padding: "0.5rem 0.75rem",
    background: "none",
    border: "none",
    borderRadius: "0.375rem",
    color,
    fontSize: "0.875rem",
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.15s",
    "--hover-bg": hbg,
  });

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          className="project-context-menu"
          role="menu"
          aria-label="Project actions"
          initial={{ opacity: 0, scale: 0.94, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: -4 }}
          transition={{ duration: 0.12 }}
          style={{
            position: "fixed",
            top,
            left,
            width: `${menuWidth}px`,
            background: menuBg,
            border: `1px solid ${menuBorder}`,
            borderRadius: "0.5rem",
            boxShadow: darkMode
              ? "0 8px 24px rgba(0,0,0,0.5)"
              : "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 9999999,
            padding: "0.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Rename */}
          <button
            id={`project-rename-${project._id}`}
            style={itemStyle()}
            className="project-ctx-item"
            role="menuitem"
            onClick={() => {
              onClose?.();
              onRename?.(project);
            }}
          >
            <Pencil size={14} style={{ flexShrink: 0 }} />
            Rename
          </button>

          {/* Archive / Unarchive */}
          <button
            id={`project-archive-${project._id}`}
            style={itemStyle()}
            className="project-ctx-item"
            role="menuitem"
            onClick={() => {
              onClose?.();
              onArchive?.(project);
            }}
          >
            {isArchived ? (
              <>
                <ArchiveRestore size={14} style={{ flexShrink: 0 }} />
                Unarchive
              </>
            ) : (
              <>
                <Archive size={14} style={{ flexShrink: 0 }} />
                Archive
              </>
            )}
          </button>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              background: menuBorder,
              margin: "0.2rem 0.5rem",
            }}
          />

          {/* Delete */}
          <button
            id={`project-delete-${project._id}`}
            style={itemStyle(dangerColor, dangerHoverBg)}
            className="project-ctx-item project-ctx-item-danger"
            role="menuitem"
            onClick={() => {
              onClose?.();
              onDelete?.(project);
            }}
          >
            <Trash2 size={14} style={{ flexShrink: 0 }} />
            Delete
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
